/**
 * TimeOffDetailsModal Component
 * Displays detailed view of a TimeOff request
 */

import React from "react";
import Modal from "react-modal";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import TimeConverter from "../../helpers/timeFormatConverter";
import UserIcon from "../../../images/icons/single-user-icon.svg";
import Type from "../../../images/icons/type.svg";
import birthIcon from "../../../images/icons/dat-of-birth.svg";
import timer from "../../../images/icons/timer.svg";
import profile_image from "../../../images/user/Default_pfp.png";
import crssrImage from "../../../images/singleinvoice/cross-red.svg";
import editImage from "../../../images/icons/edit-pen.svg";
import Delete from "../../../images/icons/delete.svg";
import cancelImage from "../../../images/singleinvoice/cancel.svg";
import tikgImage from "../../../images/singleinvoice/tick-green.svg";
import { MODAL_STYLES } from "../utils/timeOffConstants";
import TimeOffActions from "./TimeOffActions";

const TimeOffDetailsModal = ({
  isOpen,
  onClose,
  isSubmitLoading = false,
  timeOffData = null,
  user = null,
  onEdit = null,
  onDownloadDocument = null,
  onApprove = null,
  onReject = null,
  onCancel = null,
  onDelete = null,
  approveModalOpen = false,
  rejectModalOpen = false,
  cancelModalOpen = false,
  deleteModalOpen = false,
  onCancelApprove = null,
  onCancelReject = null,
  onCancelCancel = null,
  onCancelDelete = null,
  onConfirmApprove = null,
  onConfirmReject = null,
  onConfirmCancel = null,
  onConfirmDelete = null,
  SUPER_ADMIN = 1,
  ADMIN = 2,
  USER = 3,
  TEAM_MANAGER = 7,
}) => {
  if (!timeOffData) return null;

  // Extract data from timeOffData
  const profilePhoto = timeOffData.userDetails?.profile_image && 
    timeOffData.userDetails.profile_image !== "null" 
    ? timeOffData.userDetails.profile_image 
    : "";

  const profileName = timeOffData.userDetails?.first_name
    ? `${timeOffData.userDetails.first_name} ${timeOffData.userDetails.middle_name && timeOffData.userDetails.middle_name !== "null" ? " " + timeOffData.userDetails.middle_name : ""} ${timeOffData.userDetails.last_name}`
    : timeOffData.userDetails?.company_first_name 
      ? `${timeOffData.userDetails.company_first_name} ${timeOffData.userDetails.company_last_name ? timeOffData.userDetails.company_last_name : ''}` 
      : `${timeOffData.userDetails?.company_name || ''} (Company)`;

  const typeOfLeave = timeOffData.leaveDetails?.leave_type || "";
  const issueDate = moment(timeOffData.created_at).tz(global.config.getTimeZone()).format("DD/MM/YYYY");
  const startDate = moment.tz(moment(timeOffData.start_date).format('YYYY-MM-DD'), global.config.getTimeZone()).format("DD/MM/YYYY");
  const endDate = moment.tz(moment(timeOffData.end_date).format('YYYY-MM-DD'), global.config.getTimeZone()).format("DD/MM/YYYY");
  const startTime = timeOffData.start_time;
  const endTime = timeOffData.end_time;
  const uploadDocument = timeOffData.timeoff_document;
  const note = timeOffData.note;
  const torStatus = timeOffData.status;

  const verifiedByProfilePhoto = timeOffData.torVerifiedByDetails?.profile_image && 
    timeOffData.torVerifiedByDetails.profile_image !== "null" 
    ? timeOffData.torVerifiedByDetails.profile_image 
    : "";

  const verifiedByProfileName = timeOffData.torVerifiedByDetails?.first_name
    ? `${timeOffData.torVerifiedByDetails.first_name} ${timeOffData.torVerifiedByDetails.middle_name && timeOffData.torVerifiedByDetails.middle_name !== "null" ? " " + timeOffData.torVerifiedByDetails.middle_name : ""} ${timeOffData.torVerifiedByDetails.last_name}`
    : timeOffData.torVerifiedByDetails?.company_first_name 
      ? `${timeOffData.torVerifiedByDetails.company_first_name} ${timeOffData.torVerifiedByDetails.company_last_name ? timeOffData.torVerifiedByDetails.company_last_name : ''}` 
      : `${timeOffData.torVerifiedByDetails?.company_name || ''} (Company)`;

  // Calculate total days
  const calculateNumberOfDays = (startDate, startTime, endDate, endTime) => {
    try {
      if (!startTime || !endTime || startDate !== endDate) {
        const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day');
        const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day');
        const duration = moment.duration(endDateTime.diff(startDateTime));
        const numberOfDays = Math.ceil(duration.asDays());
        return `${numberOfDays + 1} days`;
      } else {
        const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day').add(startTime, 'hours');
        const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day').add(endTime, 'hours');
        const duration = moment.duration(endDateTime.diff(startDateTime));
        const numberOfDays = Math.ceil(duration.asDays());

        if (numberOfDays === 1) {
          const numberOfHours = (endTime - startTime) * numberOfDays;
          const numberOfDaysDecimal = (numberOfHours / 8).toFixed(2);
          const cappedNumberOfDaysDecimal = Math.min(numberOfDaysDecimal, 1);
          return `${cappedNumberOfDaysDecimal} days`;
        } else {
          return `${numberOfDays} days`;
        }
      }
    } catch (error) {
      console.error("Error calculating number of days:", error);
      return "0 days";
    }
  };

  const totalHours = calculateNumberOfDays(
    timeOffData.start_date,
    timeOffData.start_time,
    timeOffData.end_date,
    timeOffData.end_time
  );

  return (
    <Modal
      className="client-modal sprint-modal time-off-review time-off-review-new"
      isOpen={isOpen}
      onRequestClose={!isSubmitLoading ? onClose : undefined}
      contentLabel="Request Time Off"
      style={{
        content: {
          height: "41%",
          width: "30%",
          position: "fixed",
          top: "50%",
          left: "50%",
          parent: document.querySelector(".admin-outer.time.tracker"),
        },
      }}
    >
      <div className="modal timeoff-review">
        <div className="modal-inner-top-sec">
          <div className="modal-close-invite outter-modal-main">
            <a className="" onClick={onClose}>
              <img src={crssrImage} alt="Edit Icon" />{" "}
            </a>
          </div>
          <div className="modal-client-head">
            <h2 className="">Request Details</h2>
          </div>
        </div>
        <div className="form-field-client-outer">
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={UserIcon} alt="usericon" />
              </div>
              User
            </label>
            <div className="timeoff-user-inner">
              <span className="user-image-listingteam">
                {profilePhoto ? (
                  <img
                    src={`/uploads/lowProfileImage/${profilePhoto}`}
                    alt={profileName}
                    className="member-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/uploads/lowProfileImage/user_default.png";
                    }}
                  />
                ) : (
                  <img
                    src={profile_image}
                    alt={profileName}
                    className="member-image"
                  />
                )}
              </span>
              <span className="time-off-user-name">
                {profileName && <>{profileName} </>}
              </span>
            </div>
          </div>
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={Type} alt="usericon" />
              </div>
              Type of Leave
            </label>
            {typeOfLeave && <>{typeOfLeave}</>}
          </div>
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={birthIcon} alt="usericon" />
              </div>
              Issue Date
            </label>
            {issueDate && <>{issueDate}</>}
          </div>
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={Type} alt="usericon" />
              </div>
              Total Days
            </label>
            {totalHours && <>{totalHours}</>}
          </div>
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={birthIcon} alt="usericon" />
              </div>
              Start Date
            </label>
            {startDate && <>{moment.tz(startDate, 'DD/MM/YYYY', global.config.getTimeZone()).format('DD/MM/YYYY')}</>}
          </div>
          <div className="form-field-client">
            <label className="form-label">
              <div className="icon-before-time">
                <img src={birthIcon} alt="usericon" />
              </div>
              End Date
            </label>
            {endDate && <>{endDate}</>}
          </div>
          {startTime && (
            <div className="form-field-client">
              <label className="form-label">
                <div className="icon-before-time">
                  <img src={timer} alt="usericon" />
                </div>
                Start Time
              </label>
              {startTime && <TimeConverter time={startTime} />}
            </div>
          )}
          {endTime && (
            <div className="form-field-client">
              <label className="form-label">
                <div className="icon-before-time">
                  <img src={timer} alt="usericon" />
                </div>
                End Time
              </label>
              {endTime && <TimeConverter time={endTime} />}
            </div>
          )}
          {verifiedByProfileName && (
            <div className="form-field-client">
              <label className="form-label">
                <div className="icon-before-time">
                  <img src={UserIcon} alt="usericon" />
                </div>
                Verified By
              </label>
              <div className="timeoff-user-inner">
                <span className="user-image-listingteam">
                  {verifiedByProfilePhoto ? (
                    <img
                      src={`/uploads/lowProfileImage/${verifiedByProfilePhoto}`}
                      alt={verifiedByProfileName}
                      className="member-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/uploads/lowProfileImage/user_default.png";
                      }}
                    />
                  ) : (
                    <img
                      src={profile_image}
                      alt={verifiedByProfileName}
                      className="member-image"
                    />
                  )}
                </span>
                <span className="time-off-user-name">
                  {verifiedByProfileName && <>{verifiedByProfileName} </>}
                </span>
              </div>
            </div>
          )}
          {uploadDocument && (
            <div className="form-field-client end-time">
              <label className="form-label">
                <div className="icon-before-time">
                  <img src={Type} alt="usericon" />
                </div>
                Document
              </label>
              {uploadDocument && (
                <div
                  onClick={() => onDownloadDocument && onDownloadDocument(uploadDocument)}
                  style={{ cursor: 'pointer' }}
                >
                  {uploadDocument.length > 15 ? (
                    <span
                      className="shortened-name"
                      title={uploadDocument}
                    >
                      {uploadDocument.slice(0, 15)}...
                    </span>
                  ) : (
                    uploadDocument
                  )}
                </div>
              )}
            </div>
          )}
          <div className="form-field-client remark">
            <label className="form-label">Remarks</label>
            {note && <div className="remark-note">{note}</div>}
          </div>
          <TimeOffActions
            user={user}
            torStatus={torStatus}
            startDate={startDate}
            isSubmitLoading={isSubmitLoading}
            onEdit={onEdit}
            onApprove={onApprove}
            onReject={onReject}
            onCancel={onCancel}
            onDelete={onDelete}
            approveModalOpen={approveModalOpen}
            rejectModalOpen={rejectModalOpen}
            cancelModalOpen={cancelModalOpen}
            deleteModalOpen={deleteModalOpen}
            onCancelApprove={onCancelApprove}
            onCancelReject={onCancelReject}
            onCancelCancel={onCancelCancel}
            onCancelDelete={onCancelDelete}
            onConfirmApprove={onConfirmApprove}
            onConfirmReject={onConfirmReject}
            onConfirmCancel={onConfirmCancel}
            onConfirmDelete={onConfirmDelete}
            SUPER_ADMIN={SUPER_ADMIN}
            ADMIN={ADMIN}
            USER={USER}
            TEAM_MANAGER={TEAM_MANAGER}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TimeOffDetailsModal;
